import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, FolderPlus, MapPin, Pencil, Phone, Trash2, X } from 'lucide-react';
import { EmployeeMultiSelect, EINSATZTEAM_MULTISELECT_LABELS } from '../employees/EmployeeMultiSelect';
import { AppSelectDropdown } from '../AppSelectDropdown';
import { UserMultiSelect, PROJEKTLEITER_MULTISELECT_LABELS } from '../UserMultiSelect';
import { DatePicker } from '../DatePicker';
import { isoToLocalDateAndTime, localWallTimeToIso, shiftEndPreservingDuration } from '../../utils/activityDateTime';
import { useCompanyTimezone } from '../../hooks/useCompanyTimezone';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { useCapabilities } from '../../hooks/useCapabilities';
import { leadDetailPath } from '../../utils/leadRoutes';
import { googleMapsSearchUrl } from '../../utils/activityTypeColors';
import { isHourTrackingEntryType, normalizeBreakMinutes } from '../../utils/employeeTimeEntryHours';
import {
  isPlantafelAbsenceEntryType,
  plantafelAbsenceLabel,
  plantafelTypeSelectOptions,
} from './plantafelAbsenceMapping';
import {
  SCHEDULING_STATUS_LABELS,
  type BoardActivity,
  type BoardEmployee,
  type PlantafelCreateDraft,
  type SchedulingStatus,
} from './plantafelTypes';
import { FALLBACK_ACTIVITY_TYPES } from '../../utils/activityIcons';
import { UI_Z_CLASS } from '../../utils/uiZIndex';
import { AppConfirmDialog } from '../AppConfirmDialog';
import { ABSENCE_CONFLICT_TEXTS, useAssigneeConflictConfirm } from '../activities/useAssigneeConflictConfirm';
import { availabilityBadge, useAssigneeAvailability } from '../activities/useAssigneeAvailability';
import { useDiscardGuard } from '../../hooks/useDiscardGuard';
import { AddressAutocompleteInput } from '../AddressAutocompleteInput';
import { useChecklistsForSelect } from '../../hooks/useChecklistsForSelect';
import { buildChecklistSelectOptions } from '../../utils/checklistSelectOptions';
import {
  getDefaultChecklistIdForActivityType,
  nextChecklistIdForActivityTypeChange,
} from '../../utils/activityTypeDefaultChecklist';
import { leadSelectDropdownFields, leadSelectPrimaryLabel } from '../../utils/leadSelectOption';
import {
  ActivityRecurrenceFields,
  defaultRecurrenceForm,
  multiDaySeriesHint,
  recurrencePayloadFromForm,
} from '../activities/ActivityRecurrenceFields';
import { RecurrenceScopeDialog } from '../activities/RecurrenceScopeDialog';
import { plantafelCreatePreview, type PlantafelCreatePreview } from './plantafelCreatePreview';
import {
  applyRecurrenceChoice,
  autoMultiDaySeriesEnd,
  formatRecurrenceLabel,
  parseActivityRecurrence,
  reconcileMultiDaySeries,
  recurrenceFormFromStored,
  type MultiDaySeriesState,
  type RecurrenceFormState,
  type RecurrenceScope,
} from '../../utils/activityRecurrence';
import {
  PlantafelInlineCreateProject,
  type PlantafelCreatedLeadOption,
  type PlantafelInlineCreateProjectHandle,
} from './PlantafelInlineCreateProject';

type LeadOption = {
  id: string;
  display_id: string;
  name: string;
  lead_title?: string;
  created_at?: string;
  bestellnummer?: string | null;
  site_contact_name?: string | null;
  site_address?: string | null;
};
type MemberOption = { user_id: string; email: string; display_name: string | null };
type ActivityTypeOption = { id: string; label: string; icon?: string; default_checklist_id?: string | null };

type LeadContactInfo = {
  display_address: string | null;
  phone: string | null;
  contact_name: string | null;
  lead_title: string | null;
  assignee_user_ids: string[];
};

const STATUS_OPTIONS: { value: SchedulingStatus; label: string }[] = (
  Object.entries(SCHEDULING_STATUS_LABELS) as [SchedulingStatus, string][]
).map(([value, label]) => ({ value, label }));

function RequiredMark() {
  return <span className="text-red-500"> *</span>;
}

function memberLabel(m: MemberOption) {
  return (m.display_name && m.display_name.trim()) || m.email || m.user_id;
}

function formatScheduleLine(dateKey: string, timeStart: string, timeEnd: string): string {
  const d = new Date(`${dateKey}T12:00:00`);
  const datePart = new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
  return `${datePart} ${timeStart} bis ${timeEnd}`;
}

function telHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : '#';
}

function recurrenceFormFromActivity(
  activity: BoardActivity | null | undefined,
  startYmd: string,
): RecurrenceFormState {
  const parsed = parseActivityRecurrence(activity?.recurrence);
  const rec = parsed && 'value' in parsed ? parsed.value : null;
  return recurrenceFormFromStored(rec, startYmd);
}

function recurrenceLabelForActivity(activity: BoardActivity | null | undefined, startYmd: string): string | null {
  const parsed = parseActivityRecurrence(activity?.recurrence);
  if (!parsed || !('value' in parsed) || !parsed.value) return null;
  const label = formatRecurrenceLabel(parsed.value, undefined, startYmd);
  return label.trim() || null;
}

function buildFormFromActivity(
  activity: BoardActivity | null,
  types: ActivityTypeOption[],
  timeZone: string,
) {
  const due = isoToLocalDateAndTime(activity?.due_at ?? null, timeZone);
  let end = isoToLocalDateAndTime(activity?.end_at ?? null, timeZone);
  if (activity?.due_at && !activity.end_at) {
    const endDt = new Date(activity.due_at);
    endDt.setTime(endDt.getTime() + 3600000);
    end = isoToLocalDateAndTime(endDt.toISOString(), timeZone);
  }
  return {
    leadId: activity?.lead_id ?? '',
    title: activity?.title ?? '',
    activity_type: activity?.activity_type ?? types[0]?.id ?? 'task',
    checklist_id: activity?.checklist_id ?? null,
    dueDate: due.dateKey,
    endDate: end.dateKey,
    dueTimeStart: due.time,
    dueTimeEnd: end.time,
    notes: activity?.notes ?? '',
    done: Boolean(activity?.done),
    scheduling_status: (activity?.scheduling_status ?? 'geplant') as SchedulingStatus,
    assignee_user_ids: (activity?.assignee_users ?? []).map((u) => u.user_id),
    assignee_employee_ids: (activity?.assignee_employees ?? []).map((e) => e.id),
    break_minutes: '0',
    site_address: activity?.activity_site_address ?? '',
    recurrence: recurrenceFormFromActivity(activity, due.dateKey),
    multiDaySeries: null as MultiDaySeriesState,
  };
}

function buildFormFromCreateDraft(
  draft: PlantafelCreateDraft,
  types: ActivityTypeOption[],
  timeZone: string,
) {
  const due = isoToLocalDateAndTime(draft.due_at, timeZone);
  const end = isoToLocalDateAndTime(draft.end_at, timeZone);
  const activityType = types[0]?.id ?? 'task';
  const form = {
    leadId: draft.lead_id ?? '',
    title: '',
    activity_type: activityType,
    checklist_id: getDefaultChecklistIdForActivityType(types, activityType),
    dueDate: due.dateKey,
    endDate: end.dateKey,
    dueTimeStart: due.time,
    dueTimeEnd: end.time,
    notes: '',
    done: false,
    scheduling_status: 'bestaetigt' as SchedulingStatus,
    assignee_user_ids: [] as string[],
    assignee_employee_ids: draft.assignee_employee_ids ?? [],
    break_minutes: '0',
    site_address: '',
    recurrence: defaultRecurrenceForm(due.dateKey),
    multiDaySeries: null as MultiDaySeriesState,
  };
  // Über mehrere Tage aufgezogen: wie beim Eintippen von Ende-Datum/Uhrzeit gleich zur Serie machen.
  return isPlantafelAbsenceEntryType(activityType) ? form : reconcileMultiDaySeries(form);
}

function InfoRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-app-text-subtle">{label}</p>
      <div className="text-sm text-app-text">{children}</div>
    </div>
  );
}

function PlantafelDrawerQuickInfo({
  scheduleLine,
  recurrenceLabel,
  typeLabel,
  checklistName,
  statusLabel,
  projectTitle,
  projectSubtitle,
  leadDisplayId,
  siteAddress,
  hasCustomAddress,
  phone,
  contactName,
  employeeNames,
  userNames,
  notesPreview,
  onOpenProject,
}: {
  scheduleLine: string;
  recurrenceLabel?: string | null;
  typeLabel: string;
  checklistName?: string | null;
  statusLabel: string;
  projectTitle: string | null;
  projectSubtitle: string | null;
  leadDisplayId: string | null;
  siteAddress: string | null;
  hasCustomAddress: boolean;
  phone: string | null;
  contactName: string | null;
  employeeNames: string[];
  userNames: string[];
  notesPreview: string | null;
  onOpenProject?: () => void;
}) {
  const address = siteAddress?.trim() || '';
  const tel = phone?.trim() || '';

  return (
    <div className="space-y-3.5 rounded-lg border border-app-border bg-app-surface-muted p-3.5">
      <InfoRow label="Termin">{scheduleLine}</InfoRow>
      {recurrenceLabel ? <InfoRow label="Wiederholen">{recurrenceLabel}</InfoRow> : null}
      <InfoRow label="Typ">{typeLabel}</InfoRow>
      <InfoRow label="Status">{statusLabel}</InfoRow>

      {projectTitle ? (
        <InfoRow label="Projekt">
          {leadDisplayId && onOpenProject ? (
            <button
              type="button"
              className="text-left font-semibold text-blue-600 hover:underline"
              onClick={onOpenProject}
            >
              {projectTitle}
            </button>
          ) : (
            <p className="font-semibold">{projectTitle}</p>
          )}
          {projectSubtitle && <p className="mt-0.5 text-xs text-app-text-muted">{projectSubtitle}</p>}
        </InfoRow>
      ) : (
        <InfoRow label="Projekt">
          <span className="text-app-text-subtle">Kein Projekt verknüpft</span>
        </InfoRow>
      )}

      {address ? (
        <InfoRow label="Baustelle">
          <p className="flex items-start gap-1.5">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden />
            <span>{address}</span>
          </p>
          {hasCustomAddress ? (
            <p className="mt-1 text-xs text-app-text-subtle">Abweichend vom Projekt</p>
          ) : null}
          <a
            href={googleMapsSearchUrl(address)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 font-medium text-blue-600 hover:underline"
          >
            In Google Maps öffnen
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        </InfoRow>
      ) : (
        <InfoRow label="Baustelle">
          <span className="text-app-text-subtle">Keine Baustellenadresse hinterlegt</span>
        </InfoRow>
      )}

      {tel ? (
        <InfoRow label="Telefon">
          <a href={telHref(tel)} className="inline-flex items-center gap-1.5 font-semibold text-blue-600 hover:underline">
            <Phone className="h-4 w-4 shrink-0" aria-hidden />
            {tel}
          </a>
          {contactName && <p className="mt-0.5 text-xs text-app-text-muted">{contactName}</p>}
        </InfoRow>
      ) : (
        <InfoRow label="Telefon">
          <span className="text-app-text-subtle">Keine Telefonnummer hinterlegt</span>
        </InfoRow>
      )}

      <InfoRow label="Einsatzteam">
        {employeeNames.length > 0 ? (
          <p>{employeeNames.join(', ')}</p>
        ) : (
          <span className="text-app-text-subtle">Kein Einsatzteam zugewiesen</span>
        )}
      </InfoRow>

      <InfoRow label="Projektleiter">
        {userNames.length > 0 ? (
          <p>{userNames.join(', ')}</p>
        ) : (
          <span className="text-app-text-subtle">Kein Projektleiter zugewiesen</span>
        )}
      </InfoRow>

      <InfoRow label="Checkliste">
        {checklistName ? (
          <p>{checklistName}</p>
        ) : (
          <span className="text-app-text-subtle">Keine Checkliste</span>
        )}
      </InfoRow>

      <InfoRow label="Beschreibung">
        {notesPreview ? (
          <p className="whitespace-pre-wrap leading-relaxed text-app-text-muted">{notesPreview}</p>
        ) : (
          <span className="text-app-text-subtle">—</span>
        )}
      </InfoRow>
    </div>
  );
}

export function PlantafelDetailDrawer({
  activity,
  createDraft,
  leads,
  activityTypes,
  instanceMembers,
  employees,
  onClose,
  onSaved,
  onDeleted,
  onLeadCreated,
  onCreatePreviewChange,
}: {
  activity: BoardActivity | null;
  createDraft?: PlantafelCreateDraft | null;
  /** Neuer Termin: die Termine, die „Anlegen“ erzeugen würde (Serie = einer je Tag), für die Vorschau auf der Tafel. */
  onCreatePreviewChange?: (preview: PlantafelCreatePreview | null) => void;
  leads: LeadOption[];
  activityTypes: ActivityTypeOption[];
  instanceMembers: MemberOption[];
  employees: BoardEmployee[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
  /** Nach Anlage eines neuen Projekts aus dem Projekt-Dropdown (Parent-Liste aktualisieren). */
  onLeadCreated?: (lead: LeadOption) => void;
  instancePrefix: string;
}) {
  const timeZone = useCompanyTimezone();
  const navigate = useNavigate();
  const { canOpen } = useModuleAccess();
  const { can } = useCapabilities();
  // Projektdaten und Projektanlage laufen über /leads (Modul Projekte plus Recht).
  const canViewProjects = can('web.projekte.view');
  const canCreateProjects = can('web.projekte.edit');
  const isCreate = !activity;
  // Keep the module constant as-is: a new array per render would retrigger the form-reset effect.
  const types = activityTypes.length > 0 ? activityTypes : FALLBACK_ACTIVITY_TYPES;
  const checklists = useChecklistsForSelect();

  const [editing, setEditing] = React.useState(isCreate);
  const [form, setForm] = React.useState(() =>
    createDraft && !activity
      ? buildFormFromCreateDraft(createDraft, types, timeZone)
      : buildFormFromActivity(activity, types, timeZone),
  );
  const checklistOptions = React.useMemo(
    () => buildChecklistSelectOptions(checklists, form.activity_type),
    [checklists, form.activity_type],
  );
  const selectedChecklistName = React.useMemo(() => {
    const id = activity?.checklist_id ?? form.checklist_id;
    if (!id) return null;
    return checklists.find((c) => c.id === id)?.name ?? null;
  }, [activity?.checklist_id, form.checklist_id, checklists]);
  const [saving, setSaving] = React.useState(false);
  const conflictConfirm = useAssigneeConflictConfirm();
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  /** Formular beim Öffnen bzw. Zurücksetzen — Vergleichsbasis für „ungespeicherte Eingaben“. */
  const formBaselineRef = React.useRef(form);
  // Set below once the discard guard exists; the reset effect closes an open discard question.
  const dismissDiscardRef = React.useRef<() => void>(() => {});
  const [scopeDialog, setScopeDialog] = React.useState<null | { mode: 'save' | 'delete' }>(null);
  const [leadInfo, setLeadInfo] = React.useState<LeadContactInfo | null>(null);
  const lastAddressPrefillRef = React.useRef('');
  const [createLeadOpen, setCreateLeadOpen] = React.useState(false);
  const [leadIdBeforeCreate, setLeadIdBeforeCreate] = React.useState('');
  const [createdLeads, setCreatedLeads] = React.useState<LeadOption[]>([]);
  const createProjectRef = React.useRef<PlantafelInlineCreateProjectHandle>(null);

  const projectOptions = React.useMemo(() => {
    const byId = new Map<string, LeadOption>();
    for (const lead of leads) byId.set(lead.id, lead);
    for (const lead of createdLeads) byId.set(lead.id, lead);
    return Array.from(byId.values());
  }, [leads, createdLeads]);

  React.useEffect(() => {
    setEditing(isCreate);
    const initial =
      createDraft && isCreate
        ? buildFormFromCreateDraft(createDraft, types, timeZone)
        : buildFormFromActivity(activity, types, timeZone);
    setForm(initial);
    formBaselineRef.current = initial;
    setError(null);
    setConfirmDelete(false);
    dismissDiscardRef.current();
    setScopeDialog(null);
    setCreateLeadOpen(false);
    setLeadIdBeforeCreate('');
    lastAddressPrefillRef.current = (activity?.activity_site_address ?? '').trim();
  }, [activity?.id, activity?.series_id, activity?.recurrence, isCreate, types, createDraft, timeZone]);

  const previewCallbackRef = React.useRef(onCreatePreviewChange);
  previewCallbackRef.current = onCreatePreviewChange;
  const createPreviewAbsence = isPlantafelAbsenceEntryType(form.activity_type);
  React.useEffect(() => {
    if (!isCreate) return;
    previewCallbackRef.current?.(
      plantafelCreatePreview(form, timeZone, { ignoreRecurrence: createPreviewAbsence }),
    );
  }, [
    isCreate,
    form.dueDate,
    form.endDate,
    form.dueTimeStart,
    form.dueTimeEnd,
    form.recurrence,
    createPreviewAbsence,
    timeZone,
  ]);
  React.useEffect(() => () => previewCallbackRef.current?.(null), []);

  const openCreateProject = React.useCallback(() => {
    setLeadIdBeforeCreate(form.leadId);
    setForm((f) => ({ ...f, leadId: '' }));
    setCreateLeadOpen(true);
  }, [form.leadId]);

  const cancelCreateProject = React.useCallback(() => {
    setForm((f) => ({ ...f, leadId: leadIdBeforeCreate }));
    setLeadIdBeforeCreate('');
    setCreateLeadOpen(false);
  }, [leadIdBeforeCreate]);

  const applyCreatedLead = React.useCallback(
    (option: PlantafelCreatedLeadOption) => {
      setCreatedLeads((prev) => (prev.some((l) => l.id === option.id) ? prev : [option, ...prev]));
      const nextAddress = String(option.site_address ?? '').trim();
      setForm((f) => ({ ...f, leadId: option.id, site_address: nextAddress || f.site_address }));
      if (nextAddress) lastAddressPrefillRef.current = nextAddress;
      setLeadIdBeforeCreate('');
      setCreateLeadOpen(false);
      onLeadCreated?.(option);
    },
    [onLeadCreated],
  );

  const leadId = (editing ? form.leadId : activity?.lead_id) || activity?.lead_id || form.leadId;

  React.useEffect(() => {
    if (!leadId || !canViewProjects) {
      setLeadInfo(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/leads/${encodeURIComponent(leadId)}`);
        if (!res.ok) {
          if (!cancelled) setLeadInfo(null);
          return;
        }
        const lead = (await res.json()) as {
          site_address?: string | null;
          address?: string | null;
          phone?: string | null;
          lead_title?: string | null;
          name?: string | null;
          assignee_users?: { user_id: string }[];
          baustelle?: {
            address?: string | null;
            contact?: {
              name?: string;
              first_name?: string;
              last_name?: string;
              phone?: string;
            } | null;
          } | null;
          contact?: {
            name?: string;
            first_name?: string;
            last_name?: string;
            phone?: string;
            address?: string | null;
          } | null;
        };
        if (cancelled) return;
        const siteContact = lead.baustelle?.contact;
        const projectContact = lead.contact;
        const siteContactName =
          String(siteContact?.name ?? '').trim() ||
          `${String(siteContact?.first_name ?? '').trim()} ${String(siteContact?.last_name ?? '').trim()}`.trim() ||
          '';
        const projectContactName =
          String(projectContact?.name ?? '').trim() ||
          `${String(projectContact?.first_name ?? '').trim()} ${String(projectContact?.last_name ?? '').trim()}`.trim() ||
          String(lead.name ?? '').trim() ||
          '';
        const contactName = siteContactName || projectContactName || null;
        const siteOnly =
          String(lead.baustelle?.address ?? '').trim() ||
          String(lead.site_address ?? '').trim();
        setLeadInfo({
          display_address: siteOnly || String(lead.address ?? '').trim() || null,
          phone:
            String(siteContact?.phone ?? '').trim() ||
            String(projectContact?.phone ?? '').trim() ||
            String(lead.phone ?? '').trim() ||
            null,
          contact_name: contactName,
          lead_title: String(lead.lead_title ?? '').trim() || null,
          assignee_user_ids: Array.isArray(lead.assignee_users)
            ? lead.assignee_users.map((u) => u.user_id).filter(Boolean)
            : [],
        });
      } catch {
        if (!cancelled) setLeadInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, canViewProjects]);

  const userSelectOptions = instanceMembers.map((m) => ({
    id: m.user_id,
    display_name: m.display_name,
    email: m.email,
  }));

  const viewActivityType = activity?.activity_type ?? form.activity_type;
  const viewSchedulingStatus = activity?.scheduling_status ?? form.scheduling_status;
  const isAbsenceMode = isPlantafelAbsenceEntryType(form.activity_type);
  const repeating = !isAbsenceMode && (form.recurrence ?? defaultRecurrenceForm(form.dueDate)).freq !== 'none';
  const autoSeries = !isAbsenceMode && autoMultiDaySeriesEnd(form) !== null;
  /** Date/time edits: multi-day spans (> 24 h) become a series, and back when they no longer fit. */
  const setSpan = (update: (f: typeof form) => typeof form) =>
    setForm((f) => {
      const next = update(f);
      return isPlantafelAbsenceEntryType(next.activity_type) ? next : reconcileMultiDaySeries(next);
    });

  const availability = useAssigneeAvailability({
    dueAt: form.dueDate ? localWallTimeToIso(form.dueDate, form.dueTimeStart, timeZone) : null,
    endAt: form.dueDate
      ? localWallTimeToIso(repeating ? form.dueDate : form.endDate || form.dueDate, form.dueTimeEnd, timeZone)
      : null,
    excludeActivityId: activity?.id ?? null,
    enabled: editing && !isAbsenceMode,
  });
  const employeeSelectOptions = employees.map((e) => ({
    id: e.id,
    name: e.name,
    color_idx: e.color_idx,
    avatarColor: e.color,
    initials: e.initials,
    subtitle: (e.role ?? '').trim() || undefined,
    unavailable: isAbsenceMode ? undefined : availabilityBadge(availability[e.id]),
  }));
  const activityTypeLabel =
    plantafelAbsenceLabel(viewActivityType) ??
    types.find((t) => t.id === viewActivityType)?.label ??
    viewActivityType;
  const statusLabel = SCHEDULING_STATUS_LABELS[viewSchedulingStatus];
  const typeSelectOptions = plantafelTypeSelectOptions(types);
  const showAbsenceTimes = isAbsenceMode && isHourTrackingEntryType(form.activity_type);
  const canSave =
    !saving &&
    (isAbsenceMode
      ? form.assignee_employee_ids.length === 1 && Boolean(form.dueDate)
      : Boolean(form.title.trim()));

  const selectedLead = projectOptions.find((l) => l.id === leadId);
  const siteAddress =
    activity?.lead_site_address?.trim() ||
    leadInfo?.display_address?.trim() ||
    null;
  const hasCustomAddress = Boolean(activity?.activity_site_address?.trim());
  const projectAddressHint = leadInfo?.display_address?.trim() || null;

  React.useEffect(() => {
    if (!editing && !isCreate) return;
    const next = (projectAddressHint ?? '').trim();
    setForm((f) => {
      const current = f.site_address.trim();
      const last = lastAddressPrefillRef.current.trim();
      if (current && current !== last) return f;
      lastAddressPrefillRef.current = next;
      if (current === next) return f;
      return { ...f, site_address: next };
    });
  }, [leadId, projectAddressHint, editing, isCreate]);
  const phone =
    leadInfo?.phone ??
    activity?.lead_phone ??
    null;

  const projectTitle =
    leadInfo?.lead_title ||
    activity?.lead_title?.trim() ||
    (selectedLead ? leadSelectPrimaryLabel(selectedLead) : null);
  const projectSubtitle =
    activity?.lead_name?.trim() || selectedLead?.name || leadInfo?.contact_name || null;

  const employeeNames = (activity?.assignee_employees ?? [])
    .map((e) => {
      const row = employees.find((emp) => emp.id === e.id);
      const fn = String(e.first_name ?? '').trim();
      const ln = String(e.last_name ?? '').trim();
      return row?.name?.trim() || `${fn} ${ln}`.trim() || null;
    })
    .filter(Boolean) as string[];

  const userNames = (activity?.assignee_users ?? [])
    .map((u) => memberLabel({ user_id: u.user_id, email: u.email, display_name: u.display_name }))
    .filter(Boolean);

  const scheduleDue = activity?.due_at
    ? isoToLocalDateAndTime(activity.due_at, timeZone)
    : { dateKey: form.dueDate, time: form.dueTimeStart };
  const scheduleEnd = activity?.end_at
    ? isoToLocalDateAndTime(activity.end_at, timeZone)
    : { dateKey: form.endDate, time: form.dueTimeEnd };
  const scheduleLine = formatScheduleLine(
    scheduleDue.dateKey,
    scheduleDue.time,
    scheduleEnd.time,
  );
  const recurrenceLabel = recurrenceLabelForActivity(activity, scheduleDue.dateKey);

  const notesPreview = (activity?.notes ?? form.notes).trim() || null;
  const leadDisplayId = activity?.lead_display_id ?? selectedLead?.display_id ?? null;

  const openProject = () => {
    if (!leadId) return;
    navigate(leadDetailPath({ id: leadId }));
  };

  const saveAbsence = async (confirmConflicts = false) => {
    const employeeId = form.assignee_employee_ids[0];
    if (!employeeId || !form.dueDate) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/plantafel/absences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: employeeId,
          date_from: form.dueDate,
          date_to: form.endDate || form.dueDate,
          entry_type: form.activity_type,
          start_time: showAbsenceTimes ? form.dueTimeStart || null : null,
          end_time: showAbsenceTimes ? form.dueTimeEnd || null : null,
          break_minutes: showAbsenceTimes ? normalizeBreakMinutes(Number(form.break_minutes) || 0) : 0,
          note: form.notes.trim(),
          // Converting this activity: it is removed below, so it is no conflict.
          ...(activity ? { exclude_activity_id: activity.id } : {}),
          ...(confirmConflicts ? { confirm_conflicts: true } : {}),
        }),
      });
      if (await conflictConfirm.intercept(res, () => saveAbsence(true), undefined, ABSENCE_CONFLICT_TEXTS)) return;
      const data = (await res.json()) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Speichern fehlgeschlagen.');

      // Converting an existing board activity to absence: remove the lead_activity row.
      if (activity) {
        const delRes = await fetch(`/api/activities/${activity.id}`, { method: 'DELETE' });
        if (!delRes.ok) {
          const delBody = await delRes.json().catch(() => ({}));
          throw new Error(
            (delBody as { error?: string }).error ||
              'Abwesenheit gespeichert, die ursprüngliche Aktivität konnte nicht entfernt werden.',
          );
        }
        onDeleted();
        return;
      }

      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  /** `retry` comes from the conflict dialog: confirmed, and with the project created in the first attempt. */
  const save = async (scope?: RecurrenceScope, retry?: { leadId: string | null }) => {
    if (isAbsenceMode) {
      await saveAbsence();
      return;
    }
    if (!form.title.trim()) return;
    if (!isCreate && activity?.series_id && !scope) {
      setScopeDialog({ mode: 'save' });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let leadId = retry ? retry.leadId : form.leadId || null;
      if (createLeadOpen && !retry) {
        const created = await createProjectRef.current?.create();
        if (!created) throw new Error('Projekt konnte nicht erstellt werden.');
        applyCreatedLead(created);
        leadId = created.id;
      }

      const repeating = (form.recurrence ?? defaultRecurrenceForm(form.dueDate)).freq !== 'none';
      const dueAt = localWallTimeToIso(form.dueDate, form.dueTimeStart, timeZone);
      const endAt = localWallTimeToIso(repeating ? form.dueDate : form.endDate, form.dueTimeEnd, timeZone);
      const recurrence = recurrencePayloadFromForm(
        form.recurrence ?? defaultRecurrenceForm(form.dueDate),
        form.dueDate,
      );
      const body: Record<string, unknown> = {
        lead_id: leadId,
        title: form.title.trim(),
        activity_type: form.activity_type,
        due_at: dueAt,
        end_at: endAt,
        notes: form.notes.trim() || undefined,
        done: form.done,
        scheduling_status: form.scheduling_status,
        assignee_user_ids: form.assignee_user_ids,
        assignee_employee_ids: form.assignee_employee_ids,
        site_address: form.site_address.trim() || null,
        checklist_id: form.checklist_id || null,
      };
      if (retry) body.confirm_conflicts = true;
      if (isCreate) {
        if (recurrence) body.recurrence = recurrence;
      } else if (activity?.series_id || recurrence) {
        body.recurrence = recurrence ?? null;
      }
      const url = isCreate
        ? '/api/activities'
        : `/api/activities/${activity!.id}${scope ? `?scope=${scope}` : ''}`;
      const res = await fetch(url, {
        method: isCreate ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (await conflictConfirm.intercept(res, () => saveRef.current(scope, { leadId }))) return;
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error((b as { error?: string }).error || 'Speichern fehlgeschlagen.');
      }
      if (isCreate) {
        onSaved();
      } else {
        setEditing(false);
        onSaved();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
      setScopeDialog(null);
    }
  };

  // The conflict dialog retries after a re-render — use the latest form state.
  const saveRef = React.useRef(save);
  saveRef.current = save;

  const remove = async (scope?: RecurrenceScope) => {
    if (!activity) return;
    if (activity.series_id && !scope) {
      setConfirmDelete(false);
      setScopeDialog({ mode: 'delete' });
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      const qs = scope ? `?scope=${scope}` : '';
      const res = await fetch(`/api/activities/${activity.id}${qs}`, { method: 'DELETE' });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error((b as { error?: string }).error || 'Löschen fehlgeschlagen.');
      }
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
      setScopeDialog(null);
    }
  };

  const applyLeadAssignees = () => {
    const ids = leadInfo?.assignee_user_ids ?? [];
    if (!ids.length) return;
    setForm((f) => ({ ...f, assignee_user_ids: [...ids] }));
  };

  const showApplyLeadAssignees =
    Boolean(form.leadId) && (leadInfo?.assignee_user_ids.length ?? 0) > 0;

  const cancelEdit = () => {
    const reset = buildFormFromActivity(activity, types, timeZone);
    setForm(reset);
    formBaselineRef.current = reset;
    setError(null);
    setCreateLeadOpen(false);
    setLeadIdBeforeCreate('');
    setEditing(false);
  };

  /** Eingaben seit dem Öffnen? Die automatisch übernommene Projektadresse zählt nicht. */
  const hasUnsavedInput = () => {
    if (!editing) return false;
    const autoAddress = lastAddressPrefillRef.current.trim();
    const comparable = (f: typeof form) => ({
      ...f,
      site_address: f.site_address.trim() === autoAddress ? '' : f.site_address.trim(),
    });
    return JSON.stringify(comparable(form)) !== JSON.stringify(comparable(formBaselineRef.current));
  };

  /** Klick daneben, Esc, ✕: mit Eingaben erst nachfragen, sonst sofort schließen. */
  const discardGuard = useDiscardGuard({
    dirty: hasUnsavedInput() || createLeadOpen,
    onClose,
    disabled: saving || deleting,
    description: isCreate
      ? 'Die Aktivität ist noch nicht angelegt. Deine Eingaben gehen verloren.'
      : 'Deine Änderungen an der Aktivität sind noch nicht gespeichert.',
  });
  const requestClose = discardGuard.requestClose;
  dismissDiscardRef.current = discardGuard.dismiss;

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Der Verwerfen-Dialog schließt sich bei Esc selbst.
      if (discardGuard.confirmOpen) return;
      if (createLeadOpen) {
        cancelCreateProject();
        return;
      }
      if (confirmDelete) {
        setConfirmDelete(false);
        return;
      }
      if (scopeDialog) {
        if (!saving && !deleting) setScopeDialog(null);
        return;
      }
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cancelCreateProject, confirmDelete, discardGuard.confirmOpen, createLeadOpen, deleting, requestClose, saving, scopeDialog]);

  const headerTitle = isCreate
    ? isAbsenceMode
      ? 'Neue Abwesenheit'
      : 'Neue Aktivität'
    : activity?.title || 'Aktivität';

  return (
    <>
      {createPortal(
        <div
          className={`fixed inset-0 ${UI_Z_CLASS.plantafelDrawer} flex justify-end bg-black/25`}
          role="presentation"
          {...discardGuard.backdropProps}
        >
          <div
            className="relative flex h-full w-[min(400px,100%)] flex-col border-l border-app-border bg-app-surface shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="plantafel-activity-drawer-title"
            onClick={(e) => e.stopPropagation()}
          >
      <div className="flex items-start justify-between border-b border-app-border p-4">
        <div className="min-w-0 flex-1 pr-2">
          <h3 id="plantafel-activity-drawer-title" className="truncate text-lg font-semibold text-app-text">
            {headerTitle}
          </h3>
          {leadDisplayId && !editing && (
            <p className="mt-1 font-mono text-xs text-app-text-subtle">{leadDisplayId}</p>
          )}
        </div>
        <button type="button" className="rounded p-1 text-app-text-subtle hover:bg-app-hover" onClick={requestClose}>
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="app-scrollbar flex-1 space-y-4 overflow-y-auto overscroll-contain p-4">
        {error && <p className="text-sm text-red-600">{error}</p>}

        {!editing ? (
          <>
            {activity?.done && (
              <p className="inline-flex items-center gap-1.5 text-sm font-medium text-app-text-muted">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                Erledigt
              </p>
            )}
            <PlantafelDrawerQuickInfo
            scheduleLine={scheduleLine}
            recurrenceLabel={recurrenceLabel}
            typeLabel={activityTypeLabel}
            checklistName={selectedChecklistName}
            statusLabel={statusLabel}
            projectTitle={projectTitle}
            projectSubtitle={projectSubtitle}
            leadDisplayId={leadDisplayId}
            siteAddress={siteAddress}
            hasCustomAddress={hasCustomAddress}
            phone={phone}
            contactName={leadInfo?.contact_name ?? null}
            employeeNames={employeeNames}
            userNames={userNames}
            notesPreview={notesPreview}
            onOpenProject={leadId && canOpen(leadDetailPath({ id: leadId })) ? openProject : undefined}
          />
          </>
        ) : (
          <>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-app-text-muted">Typ</span>
              <AppSelectDropdown
                value={form.activity_type}
                onChange={(v) => {
                  const isAbs = isPlantafelAbsenceEntryType(v);
                  setForm((f) => ({
                    ...f,
                    activity_type: v,
                    leadId: isAbs ? '' : f.leadId,
                    title: isAbs ? '' : f.title,
                    assignee_user_ids: isAbs ? [] : f.assignee_user_ids,
                    assignee_employee_ids: isAbs
                      ? f.assignee_employee_ids.slice(-1)
                      : f.assignee_employee_ids,
                    checklist_id: isAbs
                      ? null
                      : nextChecklistIdForActivityTypeChange(
                          activityTypes,
                          f.activity_type,
                          v,
                          f.checklist_id,
                        ),
                  }));
                }}
                options={typeSelectOptions}
                searchable
                searchPlaceholder="Typ suchen"
              />
            </label>

            {isAbsenceMode ? (
              <p className="text-xs text-app-text-subtle">
                Wird in der Zeiterfassung des ausgewählten Mitarbeiters gespeichert.
              </p>
            ) : null}

            {!isAbsenceMode ? (
              <>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-app-text-muted">
                    Titel
                    <RequiredMark />
                  </span>
                  <input
                    className="w-full rounded-md border border-app-border px-3 py-2 text-sm"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  />
                </label>

                {canViewProjects ? (
                  <div className="block text-sm">
                    <span className="mb-1 block font-medium text-app-text-muted">Projekt</span>
                    {createLeadOpen ? (
                      <PlantafelInlineCreateProject
                        ref={createProjectRef}
                        onCancel={cancelCreateProject}
                      />
                    ) : (
                      <AppSelectDropdown
                        value={form.leadId}
                        onChange={(v) => setForm((f) => ({ ...f, leadId: v }))}
                        searchable
                        searchPlaceholder="Projekt suchen"
                        onCreate={canCreateProjects ? openCreateProject : undefined}
                        createLabel="Neues Projekt erstellen"
                        createIcon={<FolderPlus className="h-4 w-4" />}
                        options={[
                          { value: '', label: '— Kein Projekt —' },
                          ...projectOptions.map((l) => ({
                            value: l.id,
                            ...leadSelectDropdownFields(l),
                          })),
                        ]}
                      />
                    )}
                  </div>
                ) : null}

                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-app-text-muted">Adresse</span>
                  <AddressAutocompleteInput
                    value={form.site_address}
                    onChange={(v) => setForm((f) => ({ ...f, site_address: v }))}
                    placeholder="Adresse für diesen Termin"
                    inputClassName="w-full rounded-md border border-app-border px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-xs text-app-text-subtle">
                    {form.leadId
                      ? 'Vorausgefüllt mit der Projektadresse. Kann für diesen Termin geändert werden.'
                      : 'Optional. Ohne Projekt gilt nur diese Adresse.'}
                  </p>
                  {form.leadId && form.site_address.trim() && form.site_address.trim() !== (projectAddressHint ?? '') ? (
                    <button
                      type="button"
                      className="mt-1 text-xs font-medium text-blue-600 hover:underline"
                      onClick={() => {
                        const next = projectAddressHint ?? '';
                        lastAddressPrefillRef.current = next;
                        setForm((f) => ({ ...f, site_address: next }));
                      }}
                    >
                      Projektadresse verwenden
                    </button>
                  ) : null}
                  {form.site_address.trim() && !form.leadId ? (
                    <button
                      type="button"
                      className="mt-1 text-xs font-medium text-blue-600 hover:underline"
                      onClick={() => {
                        lastAddressPrefillRef.current = '';
                        setForm((f) => ({ ...f, site_address: '' }));
                      }}
                    >
                      Adresse entfernen
                    </button>
                  ) : null}
                </label>
              </>
            ) : null}

            {!isAbsenceMode ? (
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-app-text-muted">Status</span>
                <AppSelectDropdown
                  value={form.scheduling_status}
                  onChange={(v) => setForm((f) => ({ ...f, scheduling_status: v as SchedulingStatus }))}
                  options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                />
              </label>
            ) : null}

            <div className="grid grid-cols-2 gap-2">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-app-text-muted">
                  {isAbsenceMode ? 'Von' : 'Start'}
                  {isAbsenceMode ? <RequiredMark /> : null}
                </span>
                <DatePicker
                  value={form.dueDate}
                  onChange={(v) =>
                    setSpan((f) => ({
                      ...f,
                      dueDate: v,
                      endDate: f.endDate < v ? v : f.endDate,
                    }))
                  }
                />
              </label>
              {showAbsenceTimes || !isAbsenceMode ? (
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-app-text-muted">Von</span>
                  <input
                    type="time"
                    className="w-full rounded-md border border-app-border px-3 py-2 text-sm"
                    value={form.dueTimeStart}
                    onChange={(e) => {
                      const nextStartTime = e.target.value;
                      setSpan((f) => {
                        const shifted = shiftEndPreservingDuration({
                          startDate: f.dueDate,
                          endDate: f.endDate,
                          startTime: f.dueTimeStart,
                          endTime: f.dueTimeEnd,
                          nextStartTime,
                          timeZone,
                        });
                        return {
                          ...f,
                          dueTimeStart: shifted.startTime,
                          endDate: shifted.endDate,
                          dueTimeEnd: shifted.endTime,
                        };
                      });
                    }}
                  />
                </label>
              ) : (
                <div aria-hidden />
              )}
              {repeating ? (
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-app-text-muted">Endet</span>
                  <DatePicker
                    value={(form.recurrence ?? defaultRecurrenceForm(form.dueDate)).untilDate}
                    onChange={(untilDate) =>
                      setSpan((f) => ({
                        ...f,
                        recurrence: {
                          ...(f.recurrence ?? defaultRecurrenceForm(f.dueDate)),
                          untilDate,
                          endMode: 'until',
                        },
                        ...(autoMultiDaySeriesEnd(f) ? { multiDaySeries: { endYmd: untilDate } } : {}),
                      }))
                    }
                  />
                </label>
              ) : (
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-app-text-muted">
                    {isAbsenceMode ? 'Bis' : 'Ende'}
                  </span>
                  <DatePicker
                    value={form.endDate}
                    // Picking an end date again re-enables the automatic series.
                    onChange={(v) => setSpan((f) => ({ ...f, endDate: v, multiDaySeries: null }))}
                  />
                </label>
              )}
              {showAbsenceTimes || !isAbsenceMode ? (
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-app-text-muted">Bis</span>
                  <input
                    type="time"
                    className="w-full rounded-md border border-app-border px-3 py-2 text-sm"
                    value={form.dueTimeEnd}
                    onChange={(e) => setSpan((f) => ({ ...f, dueTimeEnd: e.target.value }))}
                  />
                </label>
              ) : (
                <div aria-hidden />
              )}
            </div>

            {!isAbsenceMode ? (
              <ActivityRecurrenceFields
                form={form.recurrence ?? defaultRecurrenceForm(form.dueDate)}
                onChange={(recurrence) => setForm((f) => applyRecurrenceChoice(f, recurrence))}
                hint={autoSeries ? multiDaySeriesHint(form.recurrence ?? defaultRecurrenceForm(form.dueDate)) : null}
                startYmd={form.dueDate}
                compact
              />
            ) : null}

            {showAbsenceTimes ? (
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-app-text-muted">Pause (Minuten)</span>
                <input
                  type="number"
                  min={0}
                  className="w-full rounded-md border border-app-border px-3 py-2 text-sm"
                  value={form.break_minutes ?? '0'}
                  onChange={(e) => setForm((f) => ({ ...f, break_minutes: e.target.value }))}
                />
              </label>
            ) : null}

            {!isAbsenceMode ? (
              <div className="grid grid-cols-2 items-start gap-3">
                <div className="min-w-0">
                  <span className="mb-1 block text-sm font-medium text-app-text-muted">Projektleiter</span>
                  <UserMultiSelect
                    users={userSelectOptions}
                    selected={form.assignee_user_ids}
                    onChange={(v) => setForm((f) => ({ ...f, assignee_user_ids: v }))}
                    selectionMode="explicit"
                    {...PROJEKTLEITER_MULTISELECT_LABELS}
                    className="w-full"
                    triggerClassName="inline-flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-app-border bg-app-surface px-3 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50"
                    panelAlign="start"
                    width={320}
                  />
                  {showApplyLeadAssignees ? (
                    <button
                      type="button"
                      className="mt-1.5 block text-left text-xs text-blue-600 hover:underline"
                      onClick={applyLeadAssignees}
                    >
                      Vom Projekt übernehmen
                    </button>
                  ) : null}
                </div>
                <div className="min-w-0">
                  <span className="mb-1 block text-sm font-medium text-app-text-muted">Einsatzteam</span>
                  <EmployeeMultiSelect
                    employees={employeeSelectOptions}
                    selected={form.assignee_employee_ids}
                    onChange={(v) => setForm((f) => ({ ...f, assignee_employee_ids: v }))}
                    selectionMode="explicit"
                    showSubtitle
                    className="w-full"
                    triggerClassName="inline-flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-app-border bg-app-surface px-3 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50"
                    panelAlign="end"
                    width={320}
                    {...EINSATZTEAM_MULTISELECT_LABELS}
                  />
                </div>
              </div>
            ) : (
              <div>
                <span className="mb-1 block text-sm font-medium text-app-text-muted">
                  Mitarbeiter
                  <RequiredMark />
                </span>
                <p className="mb-1 text-xs text-app-text-subtle">Genau einen Mitarbeiter auswählen.</p>
                <EmployeeMultiSelect
                  employees={employeeSelectOptions}
                  selected={form.assignee_employee_ids}
                  onChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      assignee_employee_ids: v.slice(-1),
                    }))
                  }
                  selectionMode="explicit"
                  showSubtitle
                  width={320}
                />
              </div>
            )}

            {!isAbsenceMode ? (
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-app-text-muted">Checkliste</span>
                <AppSelectDropdown
                  value={form.checklist_id ?? ''}
                  onChange={(v) => setForm((f) => ({ ...f, checklist_id: v || null }))}
                  options={checklistOptions}
                  placeholder="Keine Checkliste"
                  searchable
                  searchPlaceholder="Checkliste suchen…"
                />
              </label>
            ) : null}

            <label className="block text-sm">
              <span className="mb-1 block font-medium text-app-text-muted">Beschreibung</span>
              <textarea
                className="min-h-[80px] w-full rounded-md border border-app-border px-3 py-2 text-sm"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </label>

            {!isCreate && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.done}
                  onChange={(e) => setForm((f) => ({ ...f, done: e.target.checked }))}
                />
                Erledigt
              </label>
            )}
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-t border-app-border p-3">
        {!editing ? (
          <>
            {!isCreate && (
              <button
                type="button"
                className="btn-pipedrive-green inline-flex flex-1 items-center justify-center gap-1.5 !py-2"
                onClick={() => setEditing(true)}
              >
                <Pencil className="h-4 w-4" />
                Bearbeiten
              </button>
            )}
            {isCreate && (
              <button
                type="button"
                className="btn-pipedrive-green flex-1 !py-2"
                disabled={!canSave}
                onClick={() => void save()}
              >
                {saving ? 'Speichern…' : isAbsenceMode ? 'Abwesenheit anlegen' : 'Anlegen'}
              </button>
            )}
            {!isCreate && leadId && leadDisplayId && (
              <button
                type="button"
                className="rounded-md border border-app-border px-3 py-2 text-sm text-app-text-muted hover:bg-app-hover"
                onClick={openProject}
              >
                Projekt
              </button>
            )}
            {!isCreate && (
              <button
                type="button"
                className="rounded-md border border-app-border px-2 py-2 text-app-text-muted hover:bg-red-50 hover:text-red-600"
                title="Löschen"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn-pipedrive-green flex-1 !py-2"
              disabled={!canSave}
              onClick={() => void save()}
            >
              {saving ? 'Speichern…' : isCreate ? (isAbsenceMode ? 'Abwesenheit anlegen' : 'Anlegen') : 'Speichern'}
            </button>
            {!isCreate && (
              <button
                type="button"
                className="rounded-md border border-app-border px-3 py-2 text-sm text-app-text-muted hover:bg-app-hover"
                onClick={cancelEdit}
              >
                Abbrechen
              </button>
            )}
            {!isCreate && (
              <button
                type="button"
                className="rounded-md border border-app-border px-2 py-2 text-app-text-muted hover:bg-red-50 hover:text-red-600"
                title="Löschen"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      </div>

      {scopeDialog ? (
        <RecurrenceScopeDialog
          mode={scopeDialog.mode}
          seriesLabel="Diese Aktivität gehört zu einer Serie."
          onPick={(scope) => (scopeDialog.mode === 'delete' ? remove(scope) : save(scope))}
          onCancel={() => setScopeDialog(null)}
        />
      ) : null}

      {conflictConfirm.dialog}
      {discardGuard.dialog}
      <AppConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Aktivität wirklich löschen?"
        confirmLabel="Löschen"
        destructive
        isLoading={deleting}
        overlayClassName={UI_Z_CLASS.plantafelDrawerModal}
        onConfirm={() => void remove()}
      />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
